/**
 * Modern Collection Scroll Enhancement
 * Adds advanced scroll-triggered animations and effects
 * Note: Rewritten in pure Vanilla JS and CSS for maximum performance,
 * removing the need for GSAP and ScrollTrigger.
 */

class ModernScrollCollection {
  constructor(container) {
    this.container = container;
    this.heroImage = container.querySelector(
      ".scroll-collection-hero-background-image"
    );
    this.contentOverlay = container.querySelector(
      ".scroll-collection-content-overlay"
    );
    this.scrollIndicator = container.querySelector(
      ".scroll-collection-scroll-indicator"
    );

    if (this.container) {
      this.init();
    }
  }

  init() {
    this.setupInitialAnimations();
    this.setupScrollEffects();
    this.setupIntersectionObserver();
    this.setupParallaxEffect();
    this.addScrollIndicatorClick();
  }

  setupInitialAnimations() {
    // Animate header elements on page load
    const headerElements = this.container.querySelectorAll(
      ".scroll-collection-new-collection-badge, .scroll-collection-section-main-title, .scroll-collection-section-description"
    );

    headerElements.forEach((el, index) => {
      el.style.transform = 'translateY(30px)';
      el.style.opacity = '0';
      el.style.transition = 'transform 1s cubic-bezier(0.0, 0.0, 0.2, 1), opacity 1s cubic-bezier(0.0, 0.0, 0.2, 1)';
      el.style.transitionDelay = `${0.5 + (index * 0.2)}s`;
      
      // Trigger animation next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transform = 'translateY(0)';
          el.style.opacity = '1';
        });
      });
    });

    // Animate CTA section
    const ctaElements = this.container.querySelectorAll(
      ".scroll-collection-modern-cta-button, .scroll-collection-cta-subtext"
    );

    ctaElements.forEach((el, index) => {
      el.style.transform = 'translateY(20px)';
      el.style.opacity = '0';
      el.style.transition = 'transform 0.8s cubic-bezier(0.0, 0.0, 0.2, 1), opacity 0.8s cubic-bezier(0.0, 0.0, 0.2, 1)';
      el.style.transitionDelay = `${1.2 + (index * 0.1)}s`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transform = 'translateY(0)';
          el.style.opacity = '1';
        });
      });
    });
  }

  setupScrollEffects() {
    let ticking = false;

    window.addEventListener("scroll", () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateScrollEffects();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  updateScrollEffects() {
    const scrolled = window.pageYOffset;
    const viewportHeight = window.innerHeight;
    const scrollProgress = Math.min(scrolled / viewportHeight, 1);

    // Update hero image parallax and scale
    if (this.heroImage) {
      const scale = 1 + scrollProgress * 0.1;
      const yPos = scrolled * 0.3;
      this.heroImage.style.transform = `translate3d(0, ${yPos}px, 0) scale(${scale})`;
    }

    // Update scroll indicator opacity
    if (this.scrollIndicator) {
      const indicatorOpacity = Math.max(1 - scrollProgress * 2, 0);
      this.scrollIndicator.style.opacity = indicatorOpacity;
    }

    // Add blur effect to hero overlay as content approaches
    const heroOverlay = this.container.querySelector(
      ".scroll-collection-hero-overlay"
    );
    if (heroOverlay && scrollProgress > 0.5) {
      const blurAmount = (scrollProgress - 0.5) * 4;
      heroOverlay.style.backdropFilter = `blur(${blurAmount}px)`;
    }
  }

  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: "-10% 0px -10% 0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");

          // Special handling for products grid container
          if (
            entry.target.classList.contains(
              "scroll-collection-products-grid-container"
            )
          ) {
            entry.target.style.transition = 'transform 0.8s cubic-bezier(0.0, 0.0, 0.2, 1), opacity 0.8s cubic-bezier(0.0, 0.0, 0.2, 1)';
            entry.target.style.transform = 'translateY(0)';
            entry.target.style.opacity = '1';

            const cards = entry.target.querySelectorAll(
              ".animate-card, .scroll-collection-product-card-wrapper, .scroll-collection-feature-item"
            );

            cards.forEach((card, index) => {
              card.style.transition = 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.8s ease-out';
              card.style.transitionDelay = `${0.3 + (index * 0.15)}s`;
              
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  card.style.transform = 'translateY(0) scale(1) rotate(0)';
                  card.style.opacity = '1';
                });
              });
            });
          } else {
            const cards = entry.target.querySelectorAll(
              ".animate-card, .scroll-collection-product-card-wrapper, .scroll-collection-feature-item"
            );

            cards.forEach((card, index) => {
              card.style.transition = 'transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.8s ease-out';
              card.style.transitionDelay = `${index * 0.15}s`;
              
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  card.style.transform = 'translateY(0) scale(1) rotate(0)';
                  card.style.opacity = '1';
                  card.classList.add("animate-in", "in-view");
                });
              });
            });
          }
          
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sectionsToObserve = [
      ".scroll-collection-section-header",
      ".scroll-collection-products-grid-container",
      ".scroll-collection-cta-section",
      ".scroll-collection-bottom-section",
    ];

    sectionsToObserve.forEach((selector) => {
      const element = this.container.querySelector(selector);
      if (element) {
        if (selector === ".scroll-collection-products-grid-container") {
          element.style.transform = 'translateY(40px)';
          element.style.opacity = '0.8';
        }
        
        const cards = element.querySelectorAll(
            ".animate-card, .scroll-collection-product-card-wrapper, .scroll-collection-feature-item"
        );
        cards.forEach((card) => {
          card.style.transform = 'translateY(60px) scale(0.8) rotate(2deg)';
          card.style.opacity = '0';
        });

        observer.observe(element);
      }
    });
  }

  setupParallaxEffect() {
    const floatingElements = this.container.querySelectorAll(
      ".scroll-collection-hero-content-overlay, .scroll-collection-new-collection-badge"
    );

    if (floatingElements.length > 0) {
      let ticking = false;
      window.addEventListener("scroll", () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrolled = window.pageYOffset;
            floatingElements.forEach((element, index) => {
              const speed = 0.05 + index * 0.02;
              const yPos = -(scrolled * speed);
              element.style.transform = `translateY(${yPos}px)`;
            });
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }
  }

  addScrollIndicatorClick() {
    if (this.scrollIndicator) {
      this.scrollIndicator.addEventListener("click", () => {
        const targetPosition = window.innerHeight;
        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      });

      this.scrollIndicator.style.cursor = "pointer";
      this.scrollIndicator.style.transition = "transform 0.3s cubic-bezier(0.0, 0.0, 0.2, 1)";

      this.scrollIndicator.addEventListener("mouseenter", () => {
        this.scrollIndicator.style.transform = "scale(1.1) translateY(-5px)";
      });

      this.scrollIndicator.addEventListener("mouseleave", () => {
        this.scrollIndicator.style.transform = "scale(1) translateY(0)";
      });
    }
  }
}

class ProductCardEnhancer {
  constructor(container) {
    this.container = container;
    this.init();
  }

  init() {
    const productCards = this.container.querySelectorAll(
      ".scroll-collection-product-card-wrapper"
    );

    productCards.forEach((card) => {
      this.enhanceCard(card);
    });
  }

  enhanceCard(card) {
    card.style.transition = 'transform 0.6s cubic-bezier(0.0, 0.0, 0.2, 1)';
    
    card.addEventListener("mouseenter", (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      card.style.transformOrigin = `${x}px ${y}px`;
      card.style.transform = "scale(1.05) translateY(-10px) perspective(1000px) rotateX(5deg) rotateY(5deg)";
      card.style.zIndex = "10";
    });

    card.addEventListener("mouseleave", () => {
      card.style.transform = "scale(1) translateY(0) perspective(1000px) rotateX(0) rotateY(0)";
      card.style.zIndex = "1";
    });
  }
}

class MouseParallax {
  constructor(container) {
    this.container = container;
    this.init();
  }

  init() {
    const parallaxElements = this.container.querySelectorAll(
      ".scroll-collection-hero-content-overlay"
    );

    if (parallaxElements.length > 0) {
      let ticking = false;
      document.addEventListener("mousemove", (e) => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const mouseX = e.clientX / window.innerWidth;
            const mouseY = e.clientY / window.innerHeight;

            parallaxElements.forEach((element, index) => {
              const speed = 10 + index * 5;
              const x = (mouseX - 0.5) * speed;
              const y = (mouseY - 0.5) * speed;

              // Need to preserve scroll translation if any, keeping it simple
              element.style.transform = `translate(${x}px, ${y}px)`;
            });
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const scrollCollections = document.querySelectorAll(
    ".scroll-collection-modern-container"
  );

  scrollCollections.forEach((container) => {
    new ModernScrollCollection(container);
    new ProductCardEnhancer(container);

    if (window.innerWidth > 768) {
      new MouseParallax(container);
    }
  });
});

window.addEventListener("resize", () => {
  if (window.innerWidth <= 768) {
    const parallaxElements = document.querySelectorAll(
      ".scroll-collection-hero-content-overlay, .scroll-collection-scroll-indicator"
    );
    parallaxElements.forEach((element) => {
      element.style.transform = "";
    });
  }
});
